import React, { useState, useEffect } from 'react';
import { DollarSign, Edit, Save, X, Link as LinkIcon, ExternalLink } from 'lucide-react';
import { settingsAPI } from '../services/api';
import { toast } from 'sonner';

export const PacksManager = () => {
  const [packs, setPacks] = useState([]);
  const [editingPack, setEditingPack] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadPacks();
  }, []);

  const loadPacks = async () => {
    try {
      const loadedPacks = await settingsAPI.getPacks();
      setPacks(loadedPacks || []);
    } catch (error) {
      console.error('Error loading packs:', error);
    }
  };

  const handleEdit = (pack) => {
    setEditingPack(pack.id);
    setEditForm({
      name: pack.name,
      price: pack.price,
      duration: pack.duration,
      essaisIA: pack.essais_ia || pack.essaisIA || 0,
      paymentLink: pack.payment_link || pack.paymentLink || '',
      features: (pack.features || []).join('\n')
    });
  };

  const handleSave = async (packId) => {
    try {
      const result = await settingsAPI.updatePack(packId, {
        name: editForm.name,
        price: parseFloat(editForm.price),
        duration: editForm.duration,
        essais_ia: parseInt(editForm.essaisIA),
        payment_link: editForm.paymentLink,
        features: editForm.features.split('\n').filter(f => f.trim())
      });

      if (result.success) {
        toast.success('Pack mis à jour');
        setEditingPack(null);
        loadPacks();
      } else {
        toast.error(result.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error saving pack:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleAdd = async () => {
    try {
      const result = await settingsAPI.createPack({
        name: 'Nouveau Pack',
        price: 0,
        duration: '1 Mois',
        essais_ia: 0,
        features: []
      });

      if (result.success) {
        toast.success('Pack créé');
        loadPacks();
      } else {
        toast.error(result.message || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating pack:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleDelete = async (packId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce pack ?')) return;

    try {
      const result = await settingsAPI.deletePack(packId);
      if (result.success) {
        toast.success('Pack supprimé');
        loadPacks();
      } else {
        toast.error(result.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting pack:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Gestion des packs</h2>
          <p className="text-muted-foreground">Configurez les prix, fonctionnalités et liens de paiement de vos packs d'abonnement</p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <span>Ajouter un pack</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {packs.map((pack) => (
          <div
            key={pack.id}
            data-testid={`pack-card-${pack.id}`}
            className={`bg-card p-6 rounded-xl border-2 shadow-sm ${
              pack.popular ? 'border-primary' : 'border-transparent'
            }`}
          >
            {editingPack === pack.id ? (
              // Edit Mode
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Nom du pack</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    data-testid={`edit-name-${pack.id}`}
                    className="w-full h-10 px-3 rounded-lg border bg-background text-sm mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Prix ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.price}
                    onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                    data-testid={`edit-price-${pack.id}`}
                    className="w-full h-10 px-3 rounded-lg border bg-background text-sm mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Durée</label>
                  <input
                    type="text"
                    value={editForm.duration}
                    onChange={(e) => setEditForm({ ...editForm, duration: e.target.value })}
                    placeholder="5 Jours"
                    data-testid={`edit-duration-${pack.id}`}
                    className="w-full h-10 px-3 rounded-lg border bg-background text-sm mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Essais IA</label>
                  <input
                    type="number"
                    value={editForm.essaisIA}
                    onChange={(e) => setEditForm({ ...editForm, essaisIA: e.target.value })}
                    data-testid={`edit-essais-${pack.id}`}
                    className="w-full h-10 px-3 rounded-lg border bg-background text-sm mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <LinkIcon className="h-4 w-4 text-primary" />
                    <span>Lien de paiement (S'abonner)</span>
                  </label>
                  <input
                    type="url"
                    value={editForm.paymentLink}
                    onChange={(e) => setEditForm({ ...editForm, paymentLink: e.target.value })}
                    placeholder="https://stripe.com/pay/..."
                    data-testid={`edit-payment-link-${pack.id}`}
                    className="w-full h-10 px-3 rounded-lg border bg-background text-sm mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Lien vers la page de paiement (Stripe, PayPal, etc.)
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium">Fonctionnalités (une par ligne)</label>
                  <textarea
                    value={editForm.features}
                    onChange={(e) => setEditForm({ ...editForm, features: e.target.value })}
                    data-testid={`edit-features-${pack.id}`}
                    className="w-full px-3 py-2 rounded-lg border bg-background text-sm mt-1 min-h-[120px]"
                  />
                </div>

                <div className="flex space-x-2 pt-2">
                  <button
                    onClick={() => handleSave(pack.id)}
                    data-testid={`save-pack-${pack.id}`}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    <Save className="h-4 w-4" />
                    <span>Sauvegarder</span>
                  </button>
                  <button
                    onClick={() => setEditingPack(null)}
                    data-testid={`cancel-edit-${pack.id}`}
                    className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div className="space-y-4">
                {pack.popular && (
                  <div className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full inline-block">
                    POPULAIRE
                  </div>
                )}

                <div>
                  <h3 className="text-2xl font-bold">{pack.name}</h3>
                  <div className="flex items-baseline space-x-1 mt-2">
                    <span className="text-3xl font-bold">${pack.price}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Durée : {pack.duration}</p>
                </div>

                <div className="space-y-2">
                  {pack.features.map((feature, i) => (
                    <div key={i} className="flex items-start space-x-2 text-sm">
                      <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="h-2 w-2 rounded-full bg-primary" />
                      </div>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-secondary/50 p-3 rounded-lg text-sm">
                  <p className="font-semibold">Essais IA : {pack.essais_ia || pack.essaisIA || 0}</p>
                </div>

                {/* Payment Link Status */}
                <div className={`p-3 rounded-lg text-sm ${pack.paymentLink ? 'bg-green-500/10' : 'bg-orange-500/10'}`}>
                  <div className="flex items-center space-x-2">
                    <LinkIcon className={`h-4 w-4 ${pack.paymentLink ? 'text-green-600' : 'text-orange-600'}`} />
                    <span className={pack.paymentLink ? 'text-green-600' : 'text-orange-600'}>
                      {pack.paymentLink ? 'Lien de paiement configuré' : 'Aucun lien de paiement'}
                    </span>
                  </div>
                  {pack.paymentLink && (
                    <a 
                      href={pack.paymentLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 text-xs text-primary mt-2 hover:underline"
                    >
                      <span>Tester le lien</span>
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(pack)}
                    data-testid={`edit-pack-${pack.id}`}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Modifier</span>
                  </button>
                  <button
                    onClick={() => handleDelete(pack.id)}
                    className="px-3 py-2 rounded-lg border border-destructive text-destructive hover:bg-destructive hover:text-white transition-colors"
                    title="Supprimer le pack"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
